import { useState, useEffect } from "react";
import { useOutletContext, useParams, Link } from "react-router-dom";
import "./VendorApply.css";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { supabase } from "../../supabaseClient";
import CategorySelect from "../../components/CategorySelect";
import { Pencil, Tent, UtensilsCrossed, LayoutGrid, Globe2, Handshake, ArrowLeft } from "lucide-react";

const TENT_CATEGORIES = [
  {
    key: "standard",
    label: "Standard Tents",
    icon: Tent,
    options: [
      { value: "20x20_single", label: "20ft × 20ft — Individual", price: "KSh 90,000 + VAT", wristbands: "4", socket: "Included" },
      { value: "20x20_shared", label: "20ft × 20ft — Shared (2 vendors)", price: "KSh 55,000 + VAT", wristbands: "2 per vendor", socket: "Not Included" },
      { value: "10x10_single", label: "10ft × 10ft — Individual", price: "KSh 60,000 + VAT", wristbands: "2", socket: "Not Included" },
      { value: "pod_shared", label: "17ft × 17ft Pod (4 vendors)", price: "KSh 30,000 + VAT", wristbands: "1 per vendor", socket: "Not Included" },
    ],
  },
  {
    key: "food_court",
    label: "Food Court",
    icon: UtensilsCrossed,
    options: [
      { value: "food_court", label: "Food Court", price: "KSh 95,000 + VAT", wristbands: "4", socket: "Included" },
    ],
  },
  {
    key: "open_display",
    label: "Open Display",
    icon: LayoutGrid,
    options: [
      { value: "20x20_open", label: "20 × 20 Open Space", price: "KSh 90,000 + VAT", wristbands: "4", socket: "Not Included" },
    ],
  },
  {
    key: "partners",
    label: "Partners & Programs",
    icon: Globe2,
    options: [
      { value: "WFTO", label: "WFTO Fair Trade Zone", price: "Sponsored by WFTO", wristbands: "2 per vendor", socket: "Included" },
      { value: "sponsored", label: "Sponsored Vendor", price: "Discounted", wristbands: "Subject to confirmation", socket: "TBC" },
    ],
  },
  {
    key: "biz_boost",
    label: "Biz Boost Program",
    icon: Handshake,
    options: [
      { value: "biz_boost", label: "Biz Boost Program", price: "KSh 7,500 + VAT", wristbands: "2", socket: "Not Included" },
    ],
  },
];

const INITIAL = {
  contact_person:  "",
  brand_name:      "",
  phone_number:    "",
  whatsapp_number: "",
  email:           "",
  website:         "",
  instagram:       "",
  facebook:        "",
  kra_pin:         "",
  description:     "",
  location:        "",
  tent_space:      "",
};

function ReviewField({ label, field, type = "text", placeholder, value, editingField, onEdit, onChange, onBlur, error }) {
  return (
    <div className="bbvap-review-field">
      <span className="bbvap-review-label">{label}</span>
      {editingField === field ? (
        type === "phone" ? (
          <PhoneInput
            defaultCountry="KE"
            value={value || ""}
            onChange={onChange}
            onBlur={onBlur}
            autoFocus
          />
        ) : type === "textarea" ? (
          <textarea
            className="bbvap-textarea"
            value={value || ""}
            autoFocus
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
          />
        ) : (
          <input
            className="bbvap-input"
            type={type}
            value={value || ""}
            autoFocus
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
          />
        )
      ) : (
        <div className="bbvap-review-value" onClick={onEdit}>
          {value || <span className="bbvap-review-empty">{placeholder || "Not set — tap to add"}</span>}
          <Pencil size={14} className="bbvap-review-edit-icon" />
        </div>
      )}
      {error && <p className="bbvap-error">{error}</p>}
    </div>
  );
}

function LocationField({ value, editingField, onEdit, onChange, onBlur, error }) {
  const isEditing = editingField === "location";
  const [mode, setMode]               = useState("search");
  const [query, setQuery]             = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching]     = useState(false);
  const [otherText, setOtherText]     = useState("");

  useEffect(() => {
    if (!isEditing) return;
    if (value === "Online") {
      setMode("online");
    } else if (value) {
      setMode("other");
      setOtherText(value);
    } else {
      setMode("search");
    }
    setQuery("");
    setSuggestions([]);
  }, [isEditing]);

  useEffect(() => {
    if (mode !== "search" || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [query, mode]);

  const pickSuggestion = (s) => {
    onChange(s.display_name);
    setSuggestions([]);
    setQuery("");
    onBlur();
  };

  const chooseOnline = () => {
    onChange("Online");
    onBlur();
  };

  if (!isEditing) {
    return (
      <div className="bbvap-review-field">
        <span className="bbvap-review-label">Location / Town</span>
        <div className="bbvap-review-value" onClick={onEdit}>
          {value || <span className="bbvap-review-empty">Not set — tap to add</span>}
          <Pencil size={14} className="bbvap-review-edit-icon" />
        </div>
        {error && <p className="bbvap-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="bbvap-review-field">
      <span className="bbvap-review-label">Location / Town</span>

      <div className="bbvap-location-tabs">
        <button
          type="button"
          className={`bbvap-location-tab ${mode === "search" ? "bbvap-location-tab--active" : ""}`}
          onClick={() => setMode("search")}
        >
          Search
        </button>
        <button
          type="button"
          className={`bbvap-location-tab ${mode === "online" ? "bbvap-location-tab--active" : ""}`}
          onClick={chooseOnline}
        >
          Online
        </button>
        <button
          type="button"
          className={`bbvap-location-tab ${mode === "other" ? "bbvap-location-tab--active" : ""}`}
          onClick={() => setMode("other")}
        >
          Other
        </button>
      </div>

      {mode === "search" && (
        <div className="bbvap-location-search">
          <input
            className="bbvap-input"
            type="text"
            autoFocus
            placeholder="Start typing a town or area…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && <p className="bbvap-muted">Searching…</p>}
          {suggestions.length > 0 && (
            <>
              <ul className="bbvap-location-suggestions">
                {suggestions.map((s) => (
                  <li key={s.place_id} onClick={() => pickSuggestion(s)}>
                    {s.display_name}
                  </li>
                ))}
              </ul>
              <p className="bbvap-location-attribution">© OpenStreetMap contributors</p>
            </>
          )}
        </div>
      )}

      {mode === "other" && (
        <input
          className="bbvap-input"
          type="text"
          autoFocus
          placeholder="Type your location"
          value={otherText}
          onChange={(e) => {
            setOtherText(e.target.value);
            onChange(e.target.value);
          }}
          onBlur={onBlur}
        />
      )}

      {mode === "online" && (
        <p className="bbvap-muted">Saved as "Online" — no physical location needed.</p>
      )}

      {error && <p className="bbvap-error">{error}</p>}
    </div>
  );
}

export default function VendorApply() {
  const context = useOutletContext();
  const vendor = context?.vendor;
  const { eventKey } = useParams();

  const [event, setEvent]               = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [existingApp, setExistingApp]   = useState(null);

  const [step, setStep]                 = useState(1);
  const [form, setForm]                 = useState(INITIAL);
  const [categories, setCategories]     = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [errors, setErrors]             = useState({});
  const [agreed, setAgreed]             = useState(false);
  const [loading, setLoading]           = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [submitError, setSubmitError]   = useState(null);
  const [marketing, setMarketing]       = useState(false);

  useEffect(() => {
    if (!eventKey || !vendor?.id) return;

    const load = async () => {
      const { data: eventRow } = await supabase
        .from("events")
        .select("id, name, event_key, status, applications_close_at")
        .or(`event_key.eq.${eventKey},id.eq.${eventKey}`)
        .maybeSingle();

      setEvent(eventRow || null);

      if (eventRow) {
        const { data: appRow } = await supabase
          .from("vendor_applications")
          .select("id, status")
          .eq("vendor_id", vendor.id)
          .eq("event_id", eventRow.id)
          .maybeSingle();
        setExistingApp(appRow || null);
      }

      setForm((f) => ({
        ...f,
        contact_person: vendor.contact_person || "",
        brand_name:     vendor.brand_name || "",
        phone_number:   vendor.phone_number || "",
        email:          vendor.email || "",
        website:        vendor.website || "",
        instagram:      vendor.instagram || "",
        facebook:       vendor.facebook || "",
        kra_pin:        vendor.kra_pin || "",
        description:    vendor.default_description || vendor.bio || "",
      }));

      setCategories(
        Array.isArray(vendor.categories)
          ? vendor.categories.map((c) => (typeof c === "string" ? { label: c, value: c } : c))
          : []
      );

      setEventLoading(false);
    };

    load();
  }, [eventKey, vendor]);

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) {
      const e = { ...errors };
      delete e[field];
      setErrors(e);
    }
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.contact_person)    e.contact_person  = "Required";
    if (!form.brand_name)        e.brand_name      = "Required";
    if (!form.phone_number)      e.phone_number    = "Required";
    if (!form.whatsapp_number)   e.whatsapp_number = "Required";
    if (!form.email)             e.email           = "Required";
    if (!form.description)       e.description     = "Required";
    if (!form.location)          e.location        = "Required";
    if (categories.length === 0) e.categories        = "Please select at least one category";
    return e;
  };

  const handleStep1Next = () => {
    const e = validateStep1();
    if (Object.keys(e).length) { setErrors(e); return; }
    setStep(2);
    window.scrollTo(0, 0);
  };

  const handleStep2Next = () => {
    if (!form.tent_space) { setErrors({ tent_space: "Please select a space" }); return; }
    setStep(3);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!agreed) {
      alert("Please agree to the terms before submitting.");
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {
      const { data: dupe, error: dupeErr } = await supabase
        .from("vendor_applications")
        .select("id")
        .eq("vendor_id", vendor.id)
        .eq("event_id", event.id)
        .maybeSingle();

      if (dupeErr) throw dupeErr;

      if (dupe) {
        setSubmitError("You've already submitted an application for this event. We'll be in touch soon.");
        setLoading(false);
        return;
      }

      const { error: vendorUpdateErr } = await supabase
        .from("vendors")
        .update({
          contact_person:      form.contact_person,
          brand_name:          form.brand_name,
          phone_number:        form.phone_number,
          email:               form.email,
          website:             form.website   || null,
          instagram:           form.instagram || null,
          facebook:            form.facebook  || null,
          kra_pin:             form.kra_pin   || null,
          default_description: form.description,
          marketing_opt_in:    marketing,
        })
        .eq("id", vendor.id);

      if (vendorUpdateErr) throw vendorUpdateErr;

      const { error: appError } = await supabase
        .from("vendor_applications")
        .insert([{
          vendor_id:       vendor.id,
          event_id:        event.id,
          description:     form.description,
          whatsapp_number: form.whatsapp_number,
          location:        form.location,
          tent_type:       form.tent_space,
          categories:      categories.map((c) => c.label || c),
          status:          "pending",
        }]);

      if (appError) throw appError;

      setSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!vendor) {
    return (
      <div className="bbvap-page">
        <div className="bbvap-card">
          <p>Please log in to apply.</p>
        </div>
      </div>
    );
  }

  if (eventLoading) {
    return <div className="bbvap-page"><div className="bbvap-card">Loading…</div></div>;
  }

  if (!event) {
    return (
      <div className="bbvap-page">
        <div className="bbvap-card">
          <p>We couldn't find that event.</p>
          <Link to="/vendor/events" className="bbvap-back-link"><ArrowLeft size={14} /> Back to events</Link>
        </div>
      </div>
    );
  }

  if (event.status !== "applications_open") {
    return (
      <div className="bbvap-page">
        <div className="bbvap-card">
          <p>Applications for {event.name} aren't currently open.</p>
          <Link to="/vendor/events" className="bbvap-back-link"><ArrowLeft size={14} /> Back to events</Link>
        </div>
      </div>
    );
  }

  if (existingApp) {
    return (
      <div className="bbvap-page">
        <div className="bbvap-card">
          <p>You've already applied to {event.name}. Status: {existingApp.status}.</p>
          <Link to="/vendor/events" className="bbvap-back-link"><ArrowLeft size={14} /> Back to events</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="bbvap-page">
        <div className="bbvap-hero">
          <h1 className="bbvap-hero-title">Bizarre Bazaar</h1>
          <p className="bbvap-hero-sub">{event.name} · Vendor Application</p>
        </div>
        <div className="bbvap-card">
          <div className="bbvap-success">
            <div className="bbvap-success-icon">✓</div>
            <h2 className="bbvap-success-title">Application received!</h2>
            <p className="bbvap-success-sub">We'll be in touch soon.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bbvap-page">
      <div className="bbvap-hero">
        <h1 className="bbvap-hero-title">Bizarre Bazaar</h1>
        <p className="bbvap-hero-sub">{event.name} · Vendor Application</p>
      </div>

      <div className="bbvap-card">
        <div className="bbvap-step-header">
          <p className="bbvap-step-label">Step {step} of 3</p>
          <div className="bbvap-progress">
            <div className={`bbvap-progress-dot ${step >= 1 ? "bbvap-progress-dot--active" : ""}`} />
            <div className={`bbvap-progress-dot ${step >= 2 ? "bbvap-progress-dot--active" : ""}`} />
            <div className={`bbvap-progress-dot ${step >= 3 ? "bbvap-progress-dot--active" : ""}`} />
          </div>
        </div>

        {step === 1 && (
          <div className="bbvap-form">
            <p className="bbvap-section-label">Confirm your details</p>
            <p className="bbvap-muted">Tap any field to edit it before continuing.</p>

            <div className="bbvap-row">
              <ReviewField
                label="Your name"
                field="contact_person"
                value={form.contact_person}
                editingField={editingField}
                onEdit={() => setEditingField("contact_person")}
                onChange={(val) => set("contact_person", val)}
                onBlur={() => setEditingField(null)}
                error={errors.contact_person}
              />
              <ReviewField
                label="Brand name"
                field="brand_name"
                value={form.brand_name}
                editingField={editingField}
                onEdit={() => setEditingField("brand_name")}
                onChange={(val) => set("brand_name", val)}
                onBlur={() => setEditingField(null)}
                error={errors.brand_name}
              />
            </div>

            <div className="bbvap-row">
              <ReviewField
                label="Phone number"
                field="phone_number"
                type="phone"
                value={form.phone_number}
                editingField={editingField}
                onEdit={() => setEditingField("phone_number")}
                onChange={(val) => set("phone_number", val)}
                onBlur={() => setEditingField(null)}
                error={errors.phone_number}
              />
              <ReviewField
                label="WhatsApp number"
                field="whatsapp_number"
                type="phone"
                value={form.whatsapp_number}
                editingField={editingField}
                onEdit={() => setEditingField("whatsapp_number")}
                onChange={(val) => set("whatsapp_number", val)}
                onBlur={() => setEditingField(null)}
                error={errors.whatsapp_number}
              />
            </div>

            <ReviewField
              label="Email address"
              field="email"
              type="email"
              value={form.email}
              editingField={editingField}
              onEdit={() => setEditingField("email")}
              onChange={(val) => set("email", val)}
              onBlur={() => setEditingField(null)}
              error={errors.email}
            />

            <p className="bbvap-section-label">Online presence <span className="bbvap-optional">(optional)</span></p>
            <div className="bbvap-row">
              <ReviewField
                label="Website"
                field="website"
                value={form.website}
                editingField={editingField}
                onEdit={() => setEditingField("website")}
                onChange={(val) => set("website", val)}
                onBlur={() => setEditingField(null)}
                error={errors.website}
              />
              <ReviewField
                label="Instagram"
                field="instagram"
                value={form.instagram}
                editingField={editingField}
                onEdit={() => setEditingField("instagram")}
                onChange={(val) => set("instagram", val)}
                onBlur={() => setEditingField(null)}
                error={errors.instagram}
              />
            </div>
            <div className="bbvap-row">
              <ReviewField
                label="Facebook"
                field="facebook"
                value={form.facebook}
                editingField={editingField}
                onEdit={() => setEditingField("facebook")}
                onChange={(val) => set("facebook", val)}
                onBlur={() => setEditingField(null)}
                error={errors.facebook}
              />
              <ReviewField
                label="KRA PIN"
                field="kra_pin"
                placeholder="Tap to add"
                value={form.kra_pin}
                editingField={editingField}
                onEdit={() => setEditingField("kra_pin")}
                onChange={(val) => set("kra_pin", val)}
                onBlur={() => setEditingField(null)}
                error={errors.kra_pin}
              />
            </div>

            <p className="bbvap-section-label">About your business</p>
            <ReviewField
              label="Description"
              field="description"
              type="textarea"
              value={form.description}
              editingField={editingField}
              onEdit={() => setEditingField("description")}
              onChange={(val) => set("description", val)}
              onBlur={() => setEditingField(null)}
              error={errors.description}
            />
            <LocationField
              value={form.location}
              editingField={editingField}
              onEdit={() => setEditingField("location")}
              onChange={(val) => set("location", val)}
              onBlur={() => setEditingField(null)}
              error={errors.location}
            />

            <p className="bbvap-section-label">Categories</p>
            <CategorySelect
              value={categories}
              onChange={(val) => {
                setCategories(val);
                if (errors.categories) {
                  const e = { ...errors };
                  delete e.categories;
                  setErrors(e);
                }
              }}
              placeholder="Product or service categories"
            />
            {errors.categories && <p className="bbvap-error">{errors.categories}</p>}

            <button className="bbvap-btn bbvap-btn--primary" onClick={handleStep1Next}>
              Continue →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="bbvap-form">
            <p className="bbvap-section-label">Choose your space</p>
            <p className="bbvap-muted">Pick the tent or zone type that fits your business.</p>

            <div className="bbvap-category-sections">
              {TENT_CATEGORIES.map(({ key, label, icon: Icon, options }) => (
                <section key={key} className="bbvap-category-section">
                  <div className="bbvap-category-heading">
                    <Icon size={20} strokeWidth={1.75} />
                    <h3>{label}</h3>
                  </div>
                  <div className="bbvap-tent-grid">
                    {options.map((opt) => (
                      <div
                        key={opt.value}
                        className={`bbvap-tent-card ${form.tent_space === opt.value ? "bbvap-tent-card--active" : ""}`}
                        onClick={() => set("tent_space", opt.value)}
                      >
                        <p className="bbvap-tent-name">{opt.label}</p>
                        <p className="bbvap-tent-price">{opt.price}</p>
                        <p className="bbvap-wristbands">Wristbands: {opt.wristbands}</p>
                        <p className="bbvap-socket">Socket: {opt.socket}</p>

                        <div className="bbvap-tent-radio" />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {errors.tent_space && <p className="bbvap-error">{errors.tent_space}</p>}

            <div className="bbvap-actions">
              <button className="bbvap-btn bbvap-btn--ghost" onClick={() => setStep(1)}>
                ← Back
              </button>
              <button className="bbvap-btn bbvap-btn--primary" onClick={handleStep2Next}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bbvap-form">
            <div className="bbvap-terms-box">
              <ol className="bbvap-terms-list">
                <li>The vendor fee covers your tent vending space, plastic chairs and vendor identification wristbands.</li>
                <li>
                  Additional costs:
                  <ul className="bbvap-terms-sublist">
                    <li>Tables 6ft × 2ft — KShs 300 per day</li>
                    <li>Single socket — KShs 3480 inclusive of VAT for the duration of the event</li>
                    <li>Additional vendor wristbands for the duration of the event — KShs 300 inclusive of VAT</li>
                    <li>Hay bales — each KShs 500 inclusive of VAT</li>
                  </ul>
                </li>
                <li>On receiving your application we will confirm in writing whether your application has been accepted or not.</li>
                <li>We do not allow vendors to share vending space without our express permission. We select ALL vendors and allocate space based on our goals.</li>
                <li>Biz Baz Events Limited reserves the right of admission.</li>
                <li>
                  By submitting this form, you consent to Biz Baz Events Limited collecting and storing your personal data for event registration and management purposes. You may opt in to receive updates on future events. You may withdraw this consent at any time. For details see our{" "}
                  <a href="https://bizbazevents.com/Privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>.
                </li>
              </ol>
            </div>

            {submitError && (
              <div className="bbvap-submit-error">{submitError}</div>
            )}

            <label className="bbvap-checkbox-label">
              <input
                className="bbvap-checkbox-input"
                type="checkbox"
                checked={agreed}
                onChange={() => setAgreed(!agreed)}
              />
              I agree to the Bizbaz{" "}
              <a href="https://bizbazevents.com/Privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </label>
            <label className="bbvap-checkbox-label">
              <input
                className="bbvap-checkbox-input"
                type="checkbox"
                checked={marketing}
                onChange={() => setMarketing(!marketing)}
              />
              I consent to receiving promotional and marketing communications.
            </label>

            <div className="bbvap-actions">
              <button className="bbvap-btn bbvap-btn--ghost" onClick={() => setStep(2)}>
                ← Back
              </button>
              <button
                className="bbvap-btn bbvap-btn--primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Submitting…" : "Submit Application"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}